import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';

import { AnyPlayerUpcomingGames } from '#src/components/player/AnyPlayerUpcomingGames/index.tsx';
import { SearchPlayerCell } from '#src/components/searchPlayerTable/SearchPlayerCell/index.tsx';
import { AveragePlayerScoreCell } from '#src/components/searchPlayerTable/SearchPlayerStatsCells/AveragePlayerScoreCell/index.tsx';
import { ProjectionGradeCell } from '#src/components/searchPlayerTable/SearchPlayerStatsCells/ProjectionGradeCell/index.tsx';
import { SearchPlayerStatsCells } from '#src/components/searchPlayerTable/SearchPlayerStatsCells/index.tsx';
import {
  CellGrow,
  SearchTableRow,
} from '#src/components/searchPlayerTable/SearchTable/ui.tsx';
import { StartingPriceCell } from '#src/components/searchPlayerTable/StartingPriceCell/index.tsx';
import type { PlayerResultsProps } from '#src/searchCommonPlayer/AdvancedPlayerSearch/types.ts';
import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';

import type { SearchPlayerRow_anyPlayer } from './__generated__/index.graphql.ts';

type Props = Pick<PlayerResultsProps, 'watchlistId' | 'favoritesWatchlist'> & {
  anyPlayer: SearchPlayerRow_anyPlayer;
  onStatsClick: () => void;
  onListingsClick: () => void;
  rarity: Rarity;
};

export const SearchPlayerRow = ({
  anyPlayer,
  onStatsClick,
  onListingsClick,
  rarity,
  watchlistId,
  favoritesWatchlist,
}: Props) => {
  const { statsManager } = useManagePlayerSearchView();
  return (
    <SearchTableRow gap={2}>
      <SearchPlayerCell
        anyPlayer={anyPlayer}
        onStatsClick={onStatsClick}
        watchlistId={watchlistId}
        favoritesWatchlist={favoritesWatchlist}
      />
      <AnyPlayerUpcomingGames anyPlayer={anyPlayer} />
      {statsManager[PlayerSearchViewAttributes.PROJECTION_GRADE].map(
        so5FixtureEvent => (
          <ProjectionGradeCell
            key={`projectionGrade-${so5FixtureEvent}-${anyPlayer.slug}`}
            so5FixtureEvent={so5FixtureEvent}
            anyPlayer={anyPlayer}
          />
        )
      )}
      {statsManager[PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE].map(
        mode => (
          <AveragePlayerScoreCell
            key={mode}
            mode={mode}
            anyPlayer={anyPlayer}
          />
        )
      )}
      <SearchPlayerStatsCells anyPlayer={anyPlayer} />
      <CellGrow />
      <StartingPriceCell
        anyPlayer={anyPlayer}
        rarity={rarity}
        onListingsClick={onListingsClick}
      />
    </SearchTableRow>
  );
};

SearchPlayerRow.fragments = {
  anyPlayer: gql`
    fragment SearchPlayerRow_anyPlayer on AnyPlayerInterface {
      slug
      ...ProjectionGradeCell_anyPlayer
      ...SearchPlayerStatsCells_anyPlayer
      ...SearchPlayerCell_anyPlayer
      ...StartingPriceCell_anyPlayer
      ...AnyPlayerUpcomingGames_anyPlayer
      ...AveragePlayerScoreCell_anyPlayer
    }
    ${ProjectionGradeCell.fragments.anyPlayer}
    ${AnyPlayerUpcomingGames.fragments.anyPlayer}
    ${SearchPlayerCell.fragments.anyPlayer}
    ${SearchPlayerStatsCells.fragments.anyPlayer}
    ${StartingPriceCell.fragments.anyPlayer}
    ${AveragePlayerScoreCell.fragments.anyPlayer}
  ` as TypedDocumentNode<SearchPlayerRow_anyPlayer>,
};
